import React, { useMemo } from 'react';
import { ForestBackground } from './ForestBackground';
import { FONT_PRESETS, DEFAULT_FONT_PRESET } from '../../constants/typography';
import { BG_DARKEN_LEVELS, DEFAULT_BG_DARKEN, resolveBgFx } from '../../constants/backgrounds';
import { resolvePalette, computeRenderColors } from '../../constants/palette';
import { TEMPLATES, DEFAULT_TEMPLATE } from '../../templates';
import { PreviewInteractionProvider } from '../../contexts/PreviewInteractionContext';
import { migrateLayoutNudge } from '../../utils/gridSnap';

/**
 * Main slide canvas component (1080x1350 Instagram format)
 * 
 * Acts as a ROUTER — delegates content rendering to the active template's
 * layout component. Background rendering is shared by all templates.
 * 
 * Template resolution chain:
 *   1. data.template (per-slide) — highest priority
 *   2. template prop (carousel-level fallback for legacy data)
 *   3. DEFAULT_TEMPLATE ('forest-classic')
 * 
 * @param {Object} props
 * @param {import('../../types/slide').SlideData} props.data - Slide data
 * @param {number} props.index - Slide index
 * @param {boolean} props.exportSafe - Use fallback gradients for CORS-safe export
 * @param {string} props.template - Template ID (carousel-level fallback)
 * @param {function} [props.onElementClick] - Callback when a content element is clicked (interactive mode)
 */
export function SlideCanvas({ data, index, exportSafe, template = DEFAULT_TEMPLATE, logoEnabled = true, logoUrl = null, logoTitle = '', logoSubtitle = '', logoSize = null, logoIsDark = false, onElementClick, editingField, onEditChange, onEndEdit, onBackgroundClick, scale, onNudgeEnd, onReorder }) {
  // Resolve template — per-slide first, then carousel-level fallback, then default
  const effectiveTemplate = data.template || template || DEFAULT_TEMPLATE;
  const templateDef = TEMPLATES[effectiveTemplate] || TEMPLATES[DEFAULT_TEMPLATE];
  const LayoutComponent = templateDef.component;

  // Compute font preset
  const fontPreset = FONT_PRESETS[data.fontPreset ?? DEFAULT_FONT_PRESET] || FONT_PRESETS[DEFAULT_FONT_PRESET];

  // Resolve palette → pre-compute render colors (passed to all children)
  // Template contrast modifiers (e.g., { muted: 0.60, subtle: 0.40 }) adjust
  // element opacities for non-overridden, text-source elements. See palette.js.
  const templateContrast = templateDef.contrast || null;
  const palette = useMemo(() => resolvePalette(data.palette, data.paletteCustom), [data.palette, data.paletteCustom]);
  const pc = useMemo(() => computeRenderColors(palette, templateContrast), [palette, templateContrast]);

  // Independent darken: primitives3d > generative > gradient > image (each has own darken value)
  const isP3DActive = data.primitives3d && data.primitives3d.enabled;
  const isGradientActive = data.gradient && data.gradient.enabled;
  const isGenerativeActive = data.generative && data.generative.enabled;
  const darkenIndex = isP3DActive
    ? (data.primitives3d.darken ?? DEFAULT_BG_DARKEN)
    : isGenerativeActive
      ? (data.generative.darken ?? DEFAULT_BG_DARKEN)
      : isGradientActive
        ? (data.gradient.darken ?? DEFAULT_BG_DARKEN)
        : (data.bgDarken ?? DEFAULT_BG_DARKEN);
  const darkenLevel = BG_DARKEN_LEVELS[darkenIndex] || BG_DARKEN_LEVELS[DEFAULT_BG_DARKEN];

  // Resolve visual effects (overlay, vignette, fog, noise)
  const bgFx = useMemo(() => resolveBgFx(data.bgFx), [data.bgFx]);

  // Rounded corners (default: false — clean export with straight edges)
  const bgRounded = data.bgRounded === true;
  const radius = bgRounded ? 72 : 0;
  const clipPath = bgRounded ? 'inset(0 round 72px)' : 'none';

  return (
    <div
      className="relative slide-canvas-root"
      style={{
        width: 1080,
        height: 1350,
        borderRadius: radius,
        overflow: 'hidden',
        clipPath: clipPath,
        WebkitClipPath: clipPath,
        background: 'transparent',
      }}
      onClick={onBackgroundClick || undefined}
    >
      {/* Background — shared by ALL templates */}
      <ForestBackground
        variant={typeof data.bgIndex === 'number' ? data.bgIndex : index}
        customUrl={data.backgroundUrl}
        gradient={data.gradient}
        generative={data.generative}
        primitives3d={data.primitives3d}
        normies={data.normies?.enabled && data.normies?.template === 'normies-full-bg' ? data.normies : null}
        bgSolid={data.bgSolid || null}
        bgGrain={data.bgGrain || 0}
        bgCompensation={data.bgCompensation || 0}
        forceFallback={exportSafe}
        darken={darkenLevel.opacity}
        bgFx={bgFx}
      />

      {/* Template layout — positions content differently per template */}
      <PreviewInteractionProvider
        interactive={!!onElementClick}
        onElementClick={onElementClick}
        editingField={editingField || null}
        onEditChange={onEditChange || null}
        onEndEdit={onEndEdit || null}
        scale={scale || 0.35}
        layoutNudge={(() => {
          // Template-scoped nudge: read only the current template's offsets
          const migrated = migrateLayoutNudge(data.layoutNudge, effectiveTemplate);
          return migrated?.[effectiveTemplate] || null;
        })()}
        onNudgeEnd={onNudgeEnd || null}
        nudgeConstraints={templateDef.nudgeConstraints || null}
        contentOrder={data.contentOrder || null}
        onReorder={onReorder || null}
      >
        <LayoutComponent
          data={data}
          fontPreset={fontPreset}
          logoEnabled={logoEnabled}
          logoUrl={logoUrl}
          logoTitle={logoTitle}
          logoSubtitle={logoSubtitle}
          logoSize={logoSize}
          logoIsDark={logoIsDark}
          pc={pc}
        />
      </PreviewInteractionProvider>

      {/* Slide guides overlay — visible during nudge drag via CSS :has(.nudgeable-dragging) */}
      <div className="slide-nudge-guides" aria-hidden="true">
        {/* Center guides */}
        <div className="slide-guide slide-guide-center-h" />
        <div className="slide-guide slide-guide-center-v" />
        {/* Rule of thirds */}
        <div className="slide-guide slide-guide-third-h1" />
        <div className="slide-guide slide-guide-third-h2" />
        <div className="slide-guide slide-guide-third-v1" />
        <div className="slide-guide slide-guide-third-v2" />
        {/* Edge margins */}
        <div className="slide-guide slide-guide-margin-top" />
        <div className="slide-guide slide-guide-margin-bottom" />
        <div className="slide-guide slide-guide-margin-left" />
        <div className="slide-guide slide-guide-margin-right" />
      </div>
    </div>
  );
}
